import Button from '@algbra/ui/components/Button/Button'
import Caption from '@algbra/ui/components/Caption/Caption'
import Heading from '@algbra/ui/components/Heading/Heading'
import Input from '@algbra/ui/components/Input/Input'
import Permalink from '@algbra/ui/components/Permalink/Permalink'
import Select from '@algbra/ui/components/Select/Select'
import { yupResolver } from '@hookform/resolvers/yup'
import axios from 'axios'
import { useMemo, useRef, useState } from 'react'
import ReCAPTCHA from 'react-google-recaptcha'
import { Controller, useForm, useWatch } from 'react-hook-form'
import Lottie from 'react-lottie'
import * as yup from 'yup'
import Config from '../../config'
import * as animationData from '../ReferralsForm/alerts-success.json'
import styles from './BusinessWaitlistForm.module.scss'
import { countries, flagEmoji } from './countries'

const UK_POSTCODE_PATTERN = /^[A-Za-z]{1,2}\d[A-Za-z\d]?\s*\d[A-Za-z]{2}$/

const schema = yup.object().shape({
  firstName: yup.string().trim().required().label('First name'),
  lastName: yup.string().trim().required().label('Last name'),
  email: yup.string().trim().email().required().label('Email'),
  legalEntityName: yup.string().trim().required().label('Legal entity name'),
  legalEntityType: yup
    .string()
    .required()
    .nullable()
    .label('Legal entity type'),
  country: yup.string().required().nullable().label('Company HQ'),
  postcode: yup
    .string()
    .label('Postcode')
    .when('country', (country, fieldSchema) =>
      country === 'GBR'
        ? fieldSchema
            .trim()
            .required()
            .matches(UK_POSTCODE_PATTERN, 'Postcode must be a valid UK postcode')
        : fieldSchema
    ),
})

const lottieOptions = {
  loop: false,
  autoplay: true,
  animationData,
  rendererSettings: { preserveAspectRatio: 'xMidYMid slice' },
}

function WarningIcon(): JSX.Element {
  return (
    <svg
      className={styles.errorIcon}
      width="60"
      height="60"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        fill="currentColor"
        d="M12 2.5c-.7 0-1.4.4-1.8 1L1.4 19c-.4.7-.4 1.5 0 2.2.4.7 1.1 1.1 1.8 1.1h17.6c.8 0 1.5-.4 1.8-1.1.4-.7.4-1.5 0-2.2L13.8 3.5c-.4-.6-1.1-1-1.8-1zM11 9h2v6h-2V9zm0 8h2v2h-2v-2z"
      />
    </svg>
  )
}

export default function BusinessWaitlistForm(): JSX.Element {
  const [isSubmitted, setSubmitted] = useState(false)
  const [isError, setError] = useState(false)
  const [isLoading, setLoading] = useState(false)
  const recaptchaRef = useRef<ReCAPTCHA>(null)
  const legalEntityTypes = useMemo(
    () => [
      { value: 'SOLE_TRADER', label: 'Sole Trader' },
      {
        value: 'UNINCORPORATED_ASSOCIATION',
        label: 'Unincorporated Association',
      },
      { value: 'PARTNERSHIP', label: 'Partnership' },
      { value: 'LIMITED_PARTNERSHIP', label: 'Limited Partnership' },
      { value: 'TRUST', label: 'Trust' },
      { value: 'LIMITED_COMPANY', label: 'Limited Company' },
      {
        value: 'LIMITED_LIABILITY_PARTNERSHIP',
        label: 'Limited Liability Partnership',
      },
      {
        value: 'COMMUNITY_INTEREST_COMPANY',
        label: 'Community Interest Company',
      },
      {
        value: 'CHARITABLE_INCORPORATED_ORGANIZATION',
        label: 'Charitable Incorporated Organization',
      },
      { value: 'CO_OPERATIVE_SOCIETY', label: 'Co-operative Society' },
      {
        value: 'COMMUNITY_BENEFIT_SOCIETY',
        label: 'Community Benefit Society',
      },
      {
        value: 'BUILDING_SOCIETY_BUILDING',
        label: 'Building Society',
      },
      { value: 'CREDIT_UNION', label: 'Credit Union' },
      { value: 'FRIENDLY_SOCIETY', label: 'Friendly Society' },
    ],
    []
  )
  const countryItems = useMemo(() => {
    // Name first, flag last: native <select> type-ahead matches the start of
    // the option text, so a leading emoji would break typing e.g. "tur".
    const items = countries.map(({ value, alpha2, name }) => ({
      value,
      label: `${name} ${flagEmoji(alpha2)}`,
    }))
    // Pin the United Kingdom to the top of the list.
    const ukIndex = items.findIndex(item => item.value === 'GBR')
    const [uk] = items.splice(ukIndex, 1)
    return [uk, ...items]
  }, [])
  const { control, handleSubmit, getValues } = useForm({
    resolver: yupResolver(schema),
    criteriaMode: 'firstError',
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      legalEntityName: '',
      legalEntityType: null,
      country: null,
      postcode: '',
    },
  })
  const country = useWatch({ control, name: 'country' })

  const onFormValid = async () => {
    setLoading(true)
    try {
      const token = await recaptchaRef.current?.executeAsync()
      if (!token) throw new Error('Captcha verification could not be started')

      const {
        country: formCountry,
        postcode: formPostcode,
        ...formValues
      } = getValues()

      await axios.post('/.netlify/functions/businessWaitlist', {
        ...formValues,
        address: {
          country: formCountry,
          postcode:
            formCountry === 'GBR'
              ? formPostcode.trim().replace(/\s+/g, ' ').toUpperCase()
              : '00000',
        },
        customerType: 'BUSINESS',
        token,
      })
      setSubmitted(true)
      window.dataLayer = window.dataLayer || []
      window.dataLayer.push({ event: 'gtm_business_waitlist_sign_up' })
    } catch (error) {
      window.dataLayer = window.dataLayer || []
      window.dataLayer.push({
        error: error.response?.data?.error,
        event: 'business_waitlist_error',
      })
      setError(true)
    } finally {
      setLoading(false)
      recaptchaRef.current?.reset()
    }
  }

  if (isSubmitted) {
    return (
      <div
        className={styles.success}
        id="business-waitlist--success"
        role="status"
      >
        <div className={styles.intro}>
          <Heading level={2} type="lg" className={styles.title}>
            Join the waitlist
          </Heading>
          <Caption type="md" className={styles.subtitle}>
            Sign up to get early access.
          </Caption>
        </div>
        <div className={styles.successContent}>
          <Lottie options={lottieOptions} height={60} width={60} />
          <Heading level={2} type="sm" className={styles.successTitle}>
            You're on the waiting list!
          </Heading>
          <Caption type="md" className={styles.successDescription}>
            Thank you for joining the waiting list. Keep an eye on your inbox
            for the latest updates from Algbra!
          </Caption>
        </div>
      </div>
    )
  }

  if (isError) {
    return (
      <div
        className={styles.success}
        id="business-waitlist--error"
        role="alert"
      >
        <div className={styles.intro}>
          <Heading level={2} type="lg" className={styles.title}>
            Join the waitlist
          </Heading>
          <Caption type="md" className={styles.subtitle}>
            Sign up to get early access.
          </Caption>
        </div>
        <div className={styles.successContent}>
          <WarningIcon />
          <Heading level={2} type="sm" className={styles.successTitle}>
            Something went wrong
          </Heading>
          <Caption type="md" className={styles.successDescription}>
            Sorry, something went wrong while sending your request. Please try
            again, or contact our support team at{' '}
            <Permalink to="mailto:support@algbra.com" isExternal={true}>
              support@algbra.com
            </Permalink>
            .
          </Caption>
          <Button
            className={styles.retryButton}
            theme="primary"
            type="button"
            onClick={() => setError(false)}
          >
            Try again
          </Button>
        </div>
      </div>
    )
  }

  return (
    <form className={styles.form} onSubmit={handleSubmit(onFormValid)}>
      <ReCAPTCHA
        size="invisible"
        ref={recaptchaRef}
        sitekey={Config.reCaptchaKey}
      />
      <div className={styles.intro}>
        <Heading level={2} type="lg" className={styles.title}>
          Join the waitlist
        </Heading>
        <Caption type="md" className={styles.subtitle}>
          Sign up to get early access.
        </Caption>
      </div>
      <Controller
        control={control}
        name="firstName"
        render={({ field, fieldState: { error } }) => (
          <Input
            {...field}
            className={styles.field}
            placeholder="First name"
            error={error}
          />
        )}
      />
      <Controller
        control={control}
        name="lastName"
        render={({ field, fieldState: { error } }) => (
          <Input
            {...field}
            className={styles.field}
            placeholder="Last name"
            error={error}
          />
        )}
      />
      <Controller
        control={control}
        name="email"
        render={({ field, fieldState: { error } }) => (
          <Input
            {...field}
            className={styles.field}
            placeholder="Email"
            type="email"
            error={error}
          />
        )}
      />
      <Controller
        control={control}
        name="legalEntityName"
        render={({ field, fieldState: { error } }) => (
          <Input
            {...field}
            className={styles.field}
            placeholder="Legal entity name"
            error={error}
          />
        )}
      />
      <Controller
        control={control}
        name="legalEntityType"
        render={({ field, fieldState: { error } }) => (
          <Select
            {...field}
            className={styles.field}
            placeholder="Legal entity type"
            error={error}
            items={legalEntityTypes}
          />
        )}
      />
      <Controller
        control={control}
        name="country"
        render={({ field, fieldState: { error } }) => (
          <Select
            {...field}
            className={styles.field}
            placeholder="Company HQ"
            error={error}
            items={countryItems}
          />
        )}
      />
      {country === 'GBR' && (
        <Controller
          control={control}
          name="postcode"
          render={({ field, fieldState: { error } }) => (
            <Input
              {...field}
              className={styles.field}
              placeholder="Postcode"
              error={error}
            />
          )}
        />
      )}
      <Button
        className={styles.submitButton}
        block={true}
        theme="primary"
        loading={isLoading}
        type="submit"
      >
        Register
      </Button>
      <Caption type="md" className={styles.disclaimer}>
        By signing up, you agree that Algbra may send you marketing emails about
        its products and services. You can unsubscribe at any time. See our{' '}
        <Permalink to="/privacy-policy" isBlank={true}>
          Privacy Policy
        </Permalink>{' '}
        for details.
      </Caption>
    </form>
  )
}
